import React from 'react';
import {
  Box,
  Typography,
  Avatar,
  Button,
  Chip,
} from '@mui/material';
import { 
  OpenInNew as OpenInNewIcon, 
  Mail as MailIcon, 
  Phone as PhoneIcon, 
  Chat as ChatIcon,
  LinkedIn as LinkedInIcon,
  CheckCircle as CheckCircleIcon,
  Star as StarIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';

interface LeadHeaderProps {
  prospect: any;
  onOpenInbox: () => void;
}

export const LeadHeader: React.FC<LeadHeaderProps> = ({ prospect, onOpenInbox }) => {
  const displayName = prospect.full_name || `${prospect.first_name || ''} ${prospect.last_name || ''}`.trim();
  const title = prospect.title || '';
  const profileImage = prospect.profile_image || '';
  const linkedinUrl = prospect.linkedin_public_url || prospect.linkedin_url || '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* First row: Avatar and name/title */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar src={profileImage || undefined} alt={displayName} sx={{ width: 56, height: 56 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>
            {displayName || 'Unnamed Prospect'}
          </Typography>
          {title && (
            <Typography variant="body2" color="text.secondary" noWrap>
              {title}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Second row: Company tag only */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {prospect.current_company?.name && (
          <Chip icon={<BusinessIcon />} label={prospect.current_company.name} size="small" />
        )}
      </Box>

      {/* Third row: Stats and actions (left-aligned) */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'flex-start',
          width: '100%',
        }}
      >
        {typeof prospect.followers_count === 'number' && (
          <Chip icon={<LinkedInIcon />} label={prospect.followers_count} size="small" />
        )}
        {typeof prospect.connections_count === 'number' && (
          <Chip icon={<CheckCircleIcon />} label={prospect.connections_count} size="small" />
        )}
        {prospect.skills && prospect.skills.length > 0 && (
          <Chip icon={<StarIcon />} label={`${prospect.skills.length} skills`} size="small" />
        )}

        <Button variant="outlined" startIcon={<ChatIcon />} onClick={onOpenInbox}>
          Open Inbox
        </Button>
        {prospect.email && (
          <Button
            component="a"
            href={`mailto:${prospect.email}`}
            target="_blank"
            rel="noreferrer"
            variant="outlined"
            startIcon={<MailIcon />}
          >
            Email
          </Button>
        )}
        {prospect.phone && (
          <Button
            component="a"
            href={`tel:${prospect.phone}`}
            variant="outlined"
            startIcon={<PhoneIcon />}
          >
            Call
          </Button>
        )}
        {linkedinUrl && (
          <Button
            component="a"
            href={linkedinUrl}
            target="_blank"
            rel="noreferrer"
            variant="contained"
            endIcon={<OpenInNewIcon />}
          >
            LinkedIn
          </Button>
        )}
      </Box>
    </Box>
  );
};
